import React, { useState } from 'react';
import { 
    Send, Mail, Phone, MapPin, Clock, 
    MessageSquare, User, CheckCircle, 
    Facebook, Instagram, Twitter, Youtube,
    Mail as MailIcon, PhoneCall, Globe
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import Layout from '../components/layouts/Layout';
import { Helmet } from 'react-helmet';

function ContactPage() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        acceptTerms: false
    });
    
    const [formStatus, setFormStatus] = useState({
        submitted: false,
        loading: false,
        error: null
    });

    const contactInfo = [
        {
            icon: <PhoneCall size={24} />,
            title: "Call Us",
            details: ["+91 9175612798"],
            color: "from-green-500 to-cyan-500",
            bgColor: "bg-green-50",
            link: "tel:+919175612798"
        },
        {
            icon: <MailIcon size={24} />,
            title: "Email Us",
            details: ["Info@kaanshifilms.com", "support@kaanshifilms.com"],
            color: "from-green-500 to-pink-500",
            bgColor: "bg-green-50",
            link: "mailto:Info@kaanshifilms.com"
        },
        {
            icon: <MapPin size={24} />,
            title: "Visit Us",
            details: ["F-604, Om Sai Heights - 2", "Nalasopara west, Mumbai 401203"],
            color: "from-emerald-500 to-green-500",
            bgColor: "bg-emerald-50"
        },
        {
            icon: <Clock size={24} />,
            title: "Working Hours",
            details: ["Mon - Fri: 9:00 AM - 7:00 PM", "Sat: 10:00 AM - 4:00 PM"],
            color: "from-amber-500 to-orange-500",
            bgColor: "bg-amber-50"
        }
    ];

    const socialLinks = [
        { icon: <Facebook size={20} />, label: "Facebook", url: "#", color: "hover:bg-green-600" },
        { icon: <Instagram size={20} />, label: "Instagram", url: "#", color: "hover:bg-pink-600" },
        { icon: <Twitter size={20} />, label: "Twitter", url: "#", color: "hover:bg-green-400" },
        { icon: <Youtube size={20} />, label: "YouTube", url: "#", color: "hover:bg-red-600" },
        { icon: <FaWhatsapp size={20} />, label: "WhatsApp", url: "#", color: "hover:bg-green-600" },
    ];

    const subjectOptions = [
        "General Inquiry",
        "Project Discussion",
        "Service Inquiry",
        "Collaboration",
        "Feedback",
        "Career Opportunity",
        "Technical Support",
        "Other"
    ];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.acceptTerms) {
            setFormStatus({
                ...formStatus,
                error: "Please accept the terms and conditions"
            });
            return;
        }

        setFormStatus({ submitted: false, loading: true, error: null });

        setTimeout(() => {
            setFormStatus({
                submitted: true,
                loading: false,
                error: null
            });
            
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: '',
                acceptTerms: false
            });

            setTimeout(() => {
                setFormStatus(prev => ({ ...prev, submitted: false }));
            }, 5000);
        }, 2000);
    };

    return (
        <Layout>
            <Helmet>
                <title> Kaanshi Films Entertainment (KFE) | Film & Video Production House Mumbai Contact us  </title>
            </Helmet>
               <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="relative overflow-hidden bg-gradient-to-r from-green-600 via-green-600 to-indigo-700">
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-700/20 to-green-700/20"></div>
                    <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-white/10 to-transparent"></div>
                </div>
                
                <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
                    <div className="text-center text-white">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            Get In{' '}
                            <span className="bg-gradient-to-r from-yellow-300 to-amber-300 bg-clip-text text-transparent">
                                Touch
                            </span>
                        </h1>
                        <p className="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto">
                            Let's create something amazing together. Share your vision with us.
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-12 md:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    {/* Left Column - Contact Info */}
                    <div className="space-y-8">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">
                                Contact{' '}
                                <span className="text-green-600">Information</span>
                            </h2>
                            <p className="text-gray-600 mb-8">
                                Reach out to us through any of these channels. We're always ready to help you bring your ideas to life.
                            </p>
                        </div>

                        {/* Contact Cards */}
                        <div className="space-y-6">
                            {contactInfo.map((info, index) => (
                                <div 
                                    key={index}
                                    className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 rounded-xl bg-gradient-to-r ${info.color}`}>
                                            <div className="text-white">{info.icon}</div>
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-bold text-gray-900 mb-2">{info.title}</h3>
                                            <div className="space-y-1">
                                                {info.details.map((detail, idx) => (
                                                    info.link && idx === 0 ? (
                                                        <a 
                                                            key={idx}
                                                            href={info.link}
                                                            className="block text-gray-600 hover:text-green-600 transition-colors duration-300"
                                                        >
                                                            {detail}
                                                        </a>
                                                    ) : (
                                                        <p key={idx} className="text-gray-600">{detail}</p>
                                                    )
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${info.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
                                </div>
                            ))}
                        </div>

                        {/* Social Links */}
                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 p-6">
                            <h3 className="font-bold text-gray-900 mb-4">Follow Us</h3>
                            <div className="flex flex-wrap gap-3">
                                {socialLinks.map((social, index) => (
                                    <a
                                        key={index}
                                        href={social.url}
                                        aria-label={social.label}
                                        className={`p-3 rounded-xl bg-gray-100 text-gray-700 ${social.color} hover:text-white transition-all duration-300 hover:scale-110`}
                                    >
                                        {social.icon}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Middle & Right Columns - Form */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                            {/* Form Header */}
                            <div className="bg-gradient-to-r from-green-600 to-green-600 p-6 md:p-8">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-2xl md:text-3xl font-bold text-white">
                                            Send us a Message
                                        </h2>
                                        <p className="text-green-100 mt-2">
                                            Fill out the form below and we'll get back to you within 24 hours
                                        </p>
                                    </div>
                                    <div className="hidden md:block p-4 bg-white/20 backdrop-blur-sm rounded-full">
                                        <MessageSquare size={32} className="text-white" />
                                    </div>
                                </div>
                            </div>

                            {/* Success Message */}
                            {formStatus.submitted && (
                                <div className="m-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="text-green-600" size={24} />
                                        <div>
                                            <h4 className="font-bold text-green-800">Message Sent Successfully!</h4>
                                            <p className="text-green-700 text-sm">
                                                Thank you for contacting us. We'll get back to you soon.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Error Message */}
                            {formStatus.error && (
                                <div className="m-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl">
                                    <p className="text-red-700">{formStatus.error}</p>
                                </div>
                            )}

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="p-6 md:p-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Name */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            <div className="flex items-center gap-2 mb-1">
                                                <User size={16} className="text-green-600" />
                                                Full Name *
                                            </div>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Enter your full name"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                                            required
                                        />
                                    </div>

                                    {/* Email */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Mail size={16} className="text-green-600" />
                                                Email Address *
                                            </div>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="Enter your email "
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                                            required
                                        />
                                    </div>

                                    {/* Phone */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Phone size={16} className="text-green-600" />
                                                Phone Number *
                                            </div>
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="Enter your mobile number"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                                            required
                                        />
                                    </div>

                                    {/* Subject */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Subject *
                                        </label>
                                        <select
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-300 bg-white"
                                            required
                                        >
                                            <option value="">Select a subject</option>
                                            {subjectOptions.map((option, index) => (
                                                <option key={index} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Message - Full Width */}
                                    <div className="md:col-span-2 space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            <div className="flex items-center gap-2 mb-1">
                                                <MessageSquare size={16} className="text-indigo-600" />
                                                Your Message *
                                            </div>
                                        </label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            placeholder="Tell us about your project or inquiry..."
                                            rows="6"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 resize-none"
                                            required
                                        ></textarea>
                                    </div>

                                    {/* Terms Checkbox */}
                                    <div className="md:col-span-2">
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                name="acceptTerms"
                                                checked={formData.acceptTerms}
                                                onChange={handleChange}
                                                className="mt-1 w-5 h-5 rounded focus:ring-green-500 text-green-600"
                                                id="terms"
                                            />
                                            <label htmlFor="terms" className="text-sm text-gray-600">
                                                I agree to receive communications from Kaanshi Films Entertainment and accept the{' '}
                                                <a href="/privacy-policy" className="text-green-600 hover:underline">
                                                    Privacy Policy
                                                </a>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="md:col-span-2">
                                        <button
                                            type="submit"
                                            disabled={formStatus.loading}
                                            className={`w-full px-8 py-4 bg-gradient-to-r from-green-600 to-green-600 text-white font-bold rounded-xl hover:from-green-700 hover:to-green-700 transition-all duration-300 flex items-center justify-center gap-3 ${formStatus.loading ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-2xl hover:scale-[1.02]'}`}
                                        >
                                            {formStatus.loading ? (
                                                <>
                                                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <Send size={20} />
                                                    Send Message
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Additional Info */}
                        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-gradient-to-br from-green-50 to-cyan-50 rounded-xl p-5 border border-green-100">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2 bg-green-100 rounded-lg">
                                        <Clock size={18} className="text-green-600" />
                                    </div>
                                    <h4 className="font-semibold text-gray-900">Quick Response</h4>
                                </div>
                                <p className="text-sm text-gray-600">
                                    We typically respond within 2-4 hours during business days.
                                </p>
                            </div>

                            <div className="bg-gradient-to-br from-green-50 to-pink-50 rounded-xl p-5 border border-green-100">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2 bg-green-100 rounded-lg">
                                        <Globe size={18} className="text-green-600" />
                                    </div>
                                    <h4 className="font-semibold text-gray-900">Global Reach</h4>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Serving clients worldwide with projects of all scales.
                                </p>
                            </div>

                            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-5 border border-emerald-100">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <CheckCircle size={18} className="text-emerald-600" />
                                    </div>
                                    <h4 className="font-semibold text-gray-900">Confidentiality</h4>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Your information and project details are kept strictly confidential.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

           
            </div>
        </div>
        </Layout>
    );
}

export default ContactPage;